import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../../services/api";
import toastOptions from "../../../utils/toastOptions";
import { getValidator } from "../../../utils/validatorsYup";
import ICoupon from "../../../models/ICoupon";
import { CouponType } from "../../../models/enums/CouponType";
import { CouponScope } from "../../../models/enums/CouponScope";
import { dateMask, handleFocus, showCouponScope, showCouponType } from "../../../utils/masks";
import { ModalAddEdit } from "../../../containers";
import CurrencyInput from "../../../components/CurrencyInput";
import { authenticationService } from "../../../services/auth.service";
import moment from "../../../utils/momentConfig";

interface CouponAddProps {
  open: boolean;
  coupon?: ICoupon;
  scope?: string;
  idScope?: number;
  onSaved: () => void;
  onCancel: () => void;
}

export default function CouponAdd(props: CouponAddProps) {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);
  const [coupon, setCoupon] = useState<ICoupon | undefined>(undefined);
  const today = moment().startOf("day").toDate();
  const fixedScope = props.scope?.trim();
  const fixedIdScope = props.idScope;
  const shouldHideScopeField = !!fixedScope;

  const schema = yup.object().shape({
    code: yup
      .string()
      .required("Código obrigatório")
      .max(50, (val) => `Máximo ${val.max} caracteres`),
    type: yup
      .string()
      .required("Tipo obrigatório")
      .max(50, (val) => `Máximo ${val.max} caracteres`),
    value: getValidator({ isNumeric: true, numberGreaterThanZero: true }).required("Valor obrigatório"),
    minAmount: getValidator({ isNumeric: true }).nullable().notRequired(),
    maxDiscount: getValidator({ isNumeric: true }).nullable().notRequired(),
    startsAt: getValidator({ isDate: true })
      .test({
        name: "min-today",
        message: "Data inicial deve ser hoje ou posterior",
        test: (value: unknown) => {
          return !!value && moment(value).startOf("day").isSameOrAfter(today);
        }
      })
      .required("Data inicial obrigatória"),
    expiresAt: getValidator({ isOptionalDate: true })
      .required("Data final obrigatória")
      .min(yup.ref("startsAt"), "Data final deve ser maior ou igual à inicial"),
    limitUse: getValidator({ isNumeric: true }).nullable().notRequired(),
    scope: yup
      .string()
      .required("Escopo obrigatório"),
    idScope: yup.number(),
    note: yup
      .string()
      .max(500, (val) => `Máximo ${val.max} caracteres`)
  });

  const {
    register,
    handleSubmit,
    reset,
    errors,
    formState,
    setValue,
    clearErrors,
    trigger,
    watch
  } = useForm<ICoupon>({
    resolver: yupResolver(schema)
  });

  const couponTypeSelected = watch("type", coupon?.type ?? "");

  useEffect(() => {
    reset();
    setIsReadOnly(authenticationService.isReadOnly());

    if (props.coupon) {
      props.coupon.scope = props.scope ?? props.coupon?.scope;
      props.coupon.idScope = props.idScope ?? props.coupon?.idScope;
    }

    setCoupon(props.coupon);
    setModalOpen(props.open);

    if (fixedScope) {
      setValue("scope", fixedScope);
    }

    if (fixedIdScope) {
      setValue("idScope", fixedIdScope);
    }

    setValue("type", props.coupon?.type || "");
  }, [props.coupon, props.open]);

  const onSubmit = async (formData: ICoupon) => {
    try {
      if (props.coupon?.id) {
        await api.put(`coupons/${props.coupon.id}`, formData);
        toast.success("Cupom atualizado com sucesso!", toastOptions);
      } else {
        await api.post("coupons", formData);
        toast.success("Cupom cadastrado com sucesso!", toastOptions);
      }

      props.onSaved();
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleClose = () => {
    setModalOpen(false);
    clearErrors();
    reset();
    props.onCancel();
  };

  return (<ModalAddEdit<ICoupon>
    title={props.coupon?.id ? "Editar Cupom" : "Novo Cupom"}
    isOpen={modalOpen}
    closeModal={handleClose}
    handleCloseModal={handleClose}
    children={
      <form
        onReset={reset}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return handleSubmit<ICoupon>(onSubmit)(e);
        }}
      >
        <div className="uk-modal-body">
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="code">Código</label>
              <input
                className="uk-input uk-form-small uk-text-uppercase"
                type="text"
                id="code"
                defaultValue={coupon?.code}
                name="code"
                ref={register}
                autoComplete="off"
              />
              <small className="uk-text-danger">{errors.code?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="type">Tipo</label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="type"
                  name="type"
                  ref={register}
                  defaultValue={coupon?.type || ""}
                >
                  <option value="" key="couponType0">
                    Selecione...
                  </option>
                  <option value={CouponType.PERCENT} key="couponType1">{showCouponType(CouponType.PERCENT)}</option>
                  <option value={CouponType.VALUE} key="couponType2">{showCouponType(CouponType.VALUE)}</option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">{errors.type?.message}</small>
            </div>
            {!shouldHideScopeField && (
              <div className="uk-width-1-4@s">
                <label className="uk-form-label" htmlFor="scope">Escopo</label>
                <div
                  uk-form-custom="target: > * > span:first-child"
                  className="uk-width-1"
                >
                  <select
                    className="uk-form-small"
                    id="scope"
                    name="scope"
                    ref={register}
                    defaultValue={coupon?.scope || ""}
                  >
                    <option value="" key="couponScope0">
                      Selecione...
                    </option>
                    <option value={CouponScope.ORDER} key="couponScope1">{showCouponScope(CouponScope.ORDER)}</option>
                    <option value={CouponScope.EVENT} key="couponScope2">{showCouponScope(CouponScope.EVENT)}</option>
                  </select>
                  <button
                    className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                    style={{ textTransform: "none" }}
                    type="button"
                    tabIndex={-1}
                  >
                    <span></span>
                    <span uk-icon="icon: chevron-down"></span>
                  </button>
                </div>
                <small className="uk-text-danger">{errors.scope?.message}</small>
              </div>
            )}
            {shouldHideScopeField && <input type="hidden" id="scope" name="scope" ref={register} value={fixedScope} readOnly />}
            {shouldHideScopeField && <input type="hidden" id="idScope" name="idScope" ref={register} value={fixedIdScope} readOnly />}
            <div className="uk-width-1-4@s">
              {couponTypeSelected === CouponType.PERCENT ? (
                <>
                  <label className="uk-form-label" htmlFor="value">Percentual (%)</label>
                  <input
                    className="uk-input uk-form-small"
                    type="number"
                    id="value"
                    name="value"
                    ref={register}
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={isReadOnly}
                    autoComplete={"off"}
                    defaultValue={coupon?.value}
                  />
                  <small className="uk-text-danger">{errors.value?.message}</small>
                </>
              ) : (
                <CurrencyInput
                  name="value"
                  label="Valor"
                  placeholder="Valor"
                  defaultValue={coupon?.value}
                  register={register}
                  setValue={(name, value) => {
                    setValue("value", value)
                    setCoupon({...coupon, value: value} as ICoupon)
                  }}
                  disabled={isReadOnly}
                  trigger={(name) => trigger("value")}
                  error={errors.value?.message}
                />
              )}
            </div>            
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="limitUse">Limite de uso</label>
              <input
                className="uk-input uk-form-small"
                type="number"
                id="limitUse"
                name="limitUse"
                ref={register}
                min="1"
                step="1"
                defaultValue={coupon?.limitUse}
              />
              <small className="uk-text-danger">{errors.limitUse?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="startsAt">Válido de</label>
              <input
                className="uk-input uk-form-small"
                type="text"
                id="startsAt"
                name="startsAt"
                ref={register}
                placeholder="99/99/9999"
                defaultValue={coupon?.startsAt ? moment(coupon?.startsAt).format('DD/MM/YYYY') : ''}
                autoComplete={"off"}
                onFocus={handleFocus}
                onChange={(e) => {e.target.value = dateMask(e.target.value);}}
              />
              <small className="uk-text-danger">{errors.startsAt?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="expiresAt">Válido até</label>
              <input
                className="uk-input uk-form-small"
                type="text"
                id="expiresAt"
                name="expiresAt"
                ref={register}
                placeholder="99/99/9999"
                defaultValue={coupon?.expiresAt ? moment(coupon?.expiresAt).format('DD/MM/YYYY') : ''}
                autoComplete={"off"}
                onFocus={handleFocus}
                onChange={(e) => {e.target.value = dateMask(e.target.value);}}
              />
              <small className="uk-text-danger">{errors.expiresAt?.message}</small>
            </div>
            {couponTypeSelected === CouponType.PERCENT && <>
              <div className="uk-width-1-4@s">
                <CurrencyInput
                  name="minAmount"
                  label="Desconto mínimo"
                  placeholder="Desconto mínimo"
                  defaultValue={coupon?.minAmount}
                  register={register}
                  setValue={(name, value) => {
                    setValue("minAmount", value)
                    setCoupon({...coupon, minAmount: value} as ICoupon)
                  }}
                  disabled={isReadOnly}
                  trigger={(name) => trigger("minAmount")}
                  error={errors.minAmount?.message}
                />
              </div>
              <div className="uk-width-1-4@s">
                <CurrencyInput
                  name="maxDiscount"
                  label="Desconto máximo"
                  placeholder="Desconto máximo"
                  defaultValue={coupon?.maxDiscount}
                  register={register}
                  setValue={(name, value) => {
                    setValue("maxDiscount", value)
                    setCoupon({...coupon, maxDiscount: value} as ICoupon)
                  }}
                  disabled={isReadOnly}
                  trigger={(name) => trigger("maxDiscount")}
                  error={errors.maxDiscount?.message}
                />
              </div>
            </>}
            <div className="uk-width-1@s">
              <label className="uk-form-label" htmlFor="note">Observações</label>
              <textarea
                className="uk-textarea uk-form-small"
                id="note"
                name="note"
                rows={4}
                style={{ resize: "none" }}
                ref={register}
                defaultValue={coupon?.note}
              />
              <small className="uk-text-danger">{errors.note?.message}</small>
            </div>
          </div>
        </div>
        <div className="uk-modal-footer">
          <div
            className="uk-grid-small uk-child-width-1-4@s uk-flex-center uk-text-center"
            uk-grid="true"
          >
            <button
              className="uk-button uk-button-default uk-button-small uk-modal-close uk-margin-left"
              type="button"
              onClick={props.onCancel}
              disabled={formState.isSubmitting}
            >
              Cancelar
            </button>
            <button
              disabled={formState.isSubmitting || isReadOnly}
              className="uk-button uk-button-primary uk-button-small uk-margin-left"
              type="submit"
            >
              {(formState.isSubmitting) && <div uk-spinner="true; ratio:0.5"></div>}
              Salvar
            </button>
          </div>
        </div>
      </form>
    }
  />);
}
